// Monthly streaming report for October 2026.
import { defineDocument, em, link, list, p, section, table, text, } from '../../schema';
export const report202610TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-10-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-10-top-party-games-and-streamers',
    route: '/party-central/reports/2026-10-top-party-games-and-streamers',
    seo: {
        title: 'October 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for October 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'October 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'amber',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-11-02T00:00:00.000Z'),
        updatedAt: new Date('2026-11-02T00:00:00.000Z'),
        reportMonth: '2026-10',
        dataRange: {
            start: '2026-10-01',
            end: '2026-10-31',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('October had about 173,000 captured streams from 69,000 streamers across 35 languages, with daily volume similar to '),
                    link('/party-central/reports/2026-09-top-party-games-and-streamers', 'September'),
                    text('. Viewership rose 17%, and three channels played a large role: '),
                    link('https://twitch.tv/auronplay', 'auronplay'),
                    text(', '),
                    link('https://twitch.tv/eliasn97', 'eliasn97'),
                    text(', and '),
                    link('https://twitch.tv/batora324', 'バトラ'),
                    text('.'),
                ]),
                p([
                    em('nanos world'),
                    text(' kept first place in Party Score as German streamers joined the French roleplayers already there. Zenkai, the Naruto roleplay server French streamers have played on since at least April, opened a German server on October 10, and German streams of the game rose from 156 in September to 3,964. '),
                    em('Mario Kart World'),
                    text(' stayed second because one Japanese streamer, バトラ, picked it up and kept coming back, and '),
                    em('Sonic Racing: CrossWorlds'),
                    text(' reached third with three weekends of in-game events.'),
                ]),
                p([
                    em('Nintendo Switch Sports Resort'),
                    text(' reached 1,056 streamers in the 10 days after its October 22 launch. '),
                    em('Lethal Company'),
                    text(', the cooperative survival horror game, had more than twice as many streams live on Halloween as on an average October day. A few smaller horror games, such as '),
                    em('KLETKA'),
                    text(', saw a Halloween lift too.'),
                ]),
                p([
                    text('Four streamers tied for first by Party Score at 98, each in a different language: '),
                    link('https://twitch.tv/getonmylvltv', 'GetOnMyLvLTV'),
                    text(' in German, '),
                    link('https://twitch.tv/mang0', 'mang0'),
                    text(' in English, '),
                    link('https://twitch.tv/jotta', 'jotta'),
                    text(' in Portuguese, and '),
                    link('https://twitch.tv/nohoho12', 'のほほ'),
                    text(' in Japanese. GetOnMyLvLTV is a replay channel for the German streamer '),
                    link('https://twitch.tv/montanablack88', 'MontanaBlack88'),
                    text('.'),
                ]),
                p([
                    text('Our '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' explains the measures and rankings used throughout this report.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' led for the third month in a row, and its activity more than doubled: 8,347 streams from 623 streamers, up from 3,379 from 233 in September. German streamers announced the Zenkai server’s release in their stream titles, and '),
                            link('/party-central?y=language&from=2026-09-01T00%3A00%3A00.000Z&to=2026-11-01T00%3A00%3A00.000Z&game=1763263', 'German streams of the game jumped on October 10'),
                            text(' and kept climbing to the end of the month. Nearly all of the 309 German streamers were playing it for the first time. French play grew too, from 3,177 streams to 4,194.'),
                        ]),
                        p([
                            text('Most of the German newcomers stuck with it, and German streamers now draw slightly more of the game’s viewership than French streamers, 51% to 48%. '),
                            link('https://twitch.tv/onlyjson', 'onlyjson'),
                            text(', '),
                            link('https://twitch.tv/xrick75', 'xrick75'),
                            text(', '),
                            link('https://twitch.tv/jerzy', 'jerzy'),
                            text(', and '),
                            link('https://twitch.tv/tosho0', 'Tosho0'),
                            text(' drew the largest audiences, nearly half of the game’s German viewership between them.'),
                        ]),
                        p([
                            em('Mario Kart World'),
                            text(' stayed second even though 19% fewer streamers played it than in September. '),
                            link('https://twitch.tv/batora324', 'バトラ'),
                            text(' first tried it on October 7 and kept coming back through the month, with stream titles calling it a game that drives them crazy but that they can’t stop playing. Those streams drew 68% of the game’s viewership. In September, the game’s biggest audiences came from a different group of Japanese creators.'),
                        ]),
                        p([
                            em('Sonic Racing: CrossWorlds'),
                            text(' returned to the top 15 for the first time since February, thanks to '),
                            link('https://nintendowire.com/news/2026/10/02/sonic-racing-crossworlds-october-content-features-hatsune-miku-event-avatar-legends-dlc-and-more/', 'three weekends of events from Sega'),
                            text(': a Hatsune Miku festival, the World Championship Finals with Twitch Drops and a viewer badge, and an Avatar Legends festival. '),
                            link('/party-central?from=2026-09-01T00%3A00%3A00.000Z&to=2026-11-01T00%3A00%3A00.000Z&game=1135492840', 'Those weekends'),
                            text(' held more than half of the game’s October streams. Some of the activity was badge hunting, with about 8% of streams coming from channels advertising the badge. But some players stayed: by the end of October, nearly twice as many streams of the game were live at a time as in late September.'),
                        ]),
                        p([
                            em('Jackbox Party Packs'),
                            text(' returned to the top 15 at fourth, its best rank yet, on bigger audiences: 34 average viewers, its most in any month. '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text('’s Friday Jackbox streams drew 25% of the game’s viewership this month, and '),
                            em('The Jackbox Party Pack 12'),
                            text(' added a brief launch-week bump after its October 15 release.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'nanos world', '97', '8,347', '623', '13.4', '87'],
                                [
                                    '2',
                                    'Mario Kart World',
                                    '94',
                                    '4,860',
                                    '1,868',
                                    '2.6',
                                    '83',
                                ],
                                [
                                    '3',
                                    'Sonic Racing: CrossWorlds',
                                    '91',
                                    '5,732',
                                    '2,224',
                                    '2.6',
                                    '22',
                                ],
                                [
                                    '4',
                                    'Jackbox Party Packs',
                                    '91',
                                    '3,974',
                                    '1,977',
                                    '2.0',
                                    '34',
                                ],
                                [
                                    '5',
                                    'Diablo II: Resurrected',
                                    '91',
                                    '6,627',
                                    '1,181',
                                    '5.6',
                                    '15',
                                ],
                                ['6', 'Words On Stream', '91', '6,585', '527', '12.5', '14'],
                                ['7', 'Pinball', '91', '602', '149', '4.0', '20'],
                                ['8', 'Musical Vibes', '91', '649', '50', '13.0', '34'],
                                ['9', 'Among Us', '90', '12,548', '7,574', '1.7', '65'],
                                ['10', 'Fall Guys', '90', '9,594', '4,276', '2.2', '20'],
                                [
                                    '11',
                                    'Nintendo Switch Sports Resort',
                                    '90',
                                    '1,940',
                                    '1,055',
                                    '1.8',
                                    '63',
                                ],
                                [
                                    '12',
                                    'Super Mario Party Jamboree',
                                    '90',
                                    '1,090',
                                    '528',
                                    '2.1',
                                    '34',
                                ],
                                ['13', 'Crash Bash', '90', '323', '127', '2.5', '33'],
                                ['14', 'Lycans', '90', '194', '95', '2.0', '174'],
                                ['15', 'Pokémon Stadium', '90', '196', '58', '3.4', '36'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/getonmylvltv', 'GetOnMyLvLTV'),
                            text(' is '),
                            link('https://mein-mmo.de/en/twitch-montanablack-now-wants-to-be-on-air-around-the-clock-gronkh-has-shown-how-its-done,939172', 'MontanaBlack88’s second channel'),
                            text(', which reruns old streams from one of Germany’s biggest Twitch streamers around the clock. It isn’t new to this list: it was 15th in February and 12th in June. In October it reran MontanaBlack88’s '),
                            em('Fall Guys'),
                            text(', '),
                            em('Mario Kart 8 Deluxe'),
                            text(', '),
                            em('Pummel Party'),
                            text(', and '),
                            em('Among Us'),
                            text(' streams from 2020 to 2022. One '),
                            em('Pummel Party'),
                            text(' rerun on October 18 drew 3,538 viewers, raising the channel’s average viewership for the month.'),
                        ]),
                        p([
                            link('https://twitch.tv/mang0', 'mang0'),
                            text(', best known for '),
                            em('Super Smash Bros. Melee'),
                            text(', rose from 15th in August to third in September and shared first place in October, mostly streaming '),
                            em('Mario Kart Wii'),
                            text('. '),
                            link('https://twitch.tv/jotta', 'jotta'),
                            text(' made the top 15 for the first time, primarily playing '),
                            em('Words On Stream'),
                            text(' in Portuguese during Jottathon 2, a marathon stream that began in September and ran through October. '),
                            link('https://twitch.tv/nohoho12', 'のほほ'),
                            text(' has been on every list since February.'),
                        ]),
                        p([
                            link('https://twitch.tv/ken_sbi', 'ken_sbi'),
                            text(', '),
                            link('https://twitch.tv/zaiyana', 'Zaiyana'),
                            text(', and '),
                            link('https://twitch.tv/terste', 'Terste'),
                            text(' reached the top 15 for the first time. '),
                            em('Super Smash Bros. Ultimate'),
                            text(' was the go-to for ken_sbi, and '),
                            em('Among Us'),
                            text(' for Zaiyana and Terste. Each had a big moment in October. ken_sbi celebrated a spot on Twitch’s front page with a Smash challenge stream on October 6. Zaiyana’s 14-hour special on October 3 drew 2,022 viewers. Terste ran a subathon through most of the month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/getonmylvltv', 'GetOnMyLvLTV')],
                                    '98',
                                    '22',
                                    '255',
                                    '4',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/mang0', 'mang0')],
                                    '98',
                                    '29',
                                    '249',
                                    '3',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/jotta', 'jotta')],
                                    '98',
                                    '53',
                                    '176',
                                    '3',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '98',
                                    '29',
                                    '157',
                                    '5',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/hypnoshark', 'hypnoshark')],
                                    '97',
                                    '15',
                                    '463',
                                    '7',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/ken_sbi', 'ken_sbi')],
                                    '97',
                                    '15',
                                    '410',
                                    '3',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '97',
                                    '21',
                                    '216',
                                    '15',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '97',
                                    '17',
                                    '190',
                                    '6',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/eevisu', 'EEvisu')],
                                    '97',
                                    '24',
                                    '186',
                                    '3',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/ponce', 'Ponce')],
                                    '96',
                                    '12',
                                    '3,402',
                                    '3',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY')],
                                    '96',
                                    '10',
                                    '1,074',
                                    '6',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/gamescage', 'GamesCage')],
                                    '96',
                                    '11',
                                    '444',
                                    '4',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/zaiyana', 'Zaiyana')],
                                    '96',
                                    '13',
                                    '316',
                                    '7',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                    '96',
                                    '42',
                                    '153',
                                    '2',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/terste', 'Terste')],
                                    '96',
                                    '16',
                                    '145',
                                    '7',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Among Us'),
                            text(' and '),
                            em('Super Smash Bros. Ultimate'),
                            text(' led again, each with more than 12,000 streams and about as many per day as in September. '),
                            em('Lethal Company'),
                            text(' rose to fifth, with 7,831 streams, up from 6,228 in September.'),
                        ]),
                        p([
                            text('Halloween showed up in horror games, led by '),
                            em('Lethal Company'),
                            text('. The game held steady through most of October, then '),
                            link('/party-central?from=2026-09-01T00%3A00%3A00.000Z&to=2026-11-01T00%3A00%3A00.000Z&game=2085980140', 'jumped on October 31'),
                            text(', with 76% more streams live at a time than on October’s other Saturdays. More than a third of its Halloween streams mentioned the holiday in their titles. The co-op horror game '),
                            em('KLETKA'),
                            text(' also doubled its usual Saturday streams, on a much smaller scale. Party games as a whole had a quiet Halloween, with streaming 10% below October’s other Saturdays.'),
                        ]),
                        p([
                            em('Fall Guys'),
                            text(' fell for a second month, from 10,872 streams to 9,594, and '),
                            link('/party-central?measure=avgStreamsPerCapture&grain=month&from=2026-03-01T00%3A00%3A00.000Z&to=2026-11-01T00%3A00%3A00.000Z&game=512980', 'had fewer streams live at a time than in any month'),
                            text(' since we started tracking. '),
                            em('MECCHA CHAMELEON'),
                            text(' kept fading, from 10,076 to 5,656, and '),
                            em('Mimic Party'),
                            text(' fell from 3,149 to 1,693 after its strong first month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Among Us', '12,548', '7,574', '65'],
                                ['2', 'Super Smash Bros. Ultimate', '12,237', '3,624', '11'],
                                ['3', 'Fall Guys', '9,594', '4,276', '20'],
                                ['4', 'nanos world', '8,347', '623', '87'],
                                ['5', 'Lethal Company', '7,831', '4,809', '13'],
                                ['6', 'Diablo II: Resurrected', '6,627', '1,181', '15'],
                                ['7', 'Words On Stream', '6,585', '527', '14'],
                                ['8', 'Sonic Racing: CrossWorlds', '5,732', '2,224', '22'],
                                ['9', 'MECCHA CHAMELEON', '5,656', '4,382', '27'],
                                ['10', 'Mario Kart World', '4,860', '1,868', '83'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Most of the games with the biggest audiences got them from single visits by large creators. '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(' drew 86% of the viewership for '),
                            em('Bunny Guys!'),
                            text(' with one Italian rage-game night on October 6, as they did for '),
                            em('Beetleball'),
                            text(' in September. '),
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(' drew 97% of the viewership for '),
                            em('Maitre des Fleurs'),
                            text(' in a handful of French streams. One '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' stream drew about 81% of '),
                            em('Stinker Golf'),
                            text('’s.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Bunny Guys!', '1,572', '22', '22'],
                                ['2', 'Maitre des Fleurs', '1,346', '13', '7'],
                                ['3', 'Stinker Golf', '922', '15', '15'],
                                ['4', 'Real VR Fishing', '862', '21', '13'],
                                ['5', 'Who Wants to Be a Millionaire', '856', '23', '19'],
                                ['6', 'diffiCULT', '420', '12', '10'],
                                ['7', 'Deduce Together', '373', '39', '30'],
                                ['8', 'Heave Ho 2', '261', '21', '16'],
                                ['9', 'Family Feud', '257', '51', '37'],
                                ['10', 'Punch Upon a Time', '208', '119', '79'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Among Us'),
                            text(' reached the most streamers, 7,574, up from 6,999 in September. Most only dropped in: 74% were seen playing it just once. '),
                            em('Lethal Company'),
                            text(' had the second-widest reach, with 4,809 streamers, up from 3,449 in September and its most since May.'),
                        ]),
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' still had the third-widest reach, but with 4,382 streamers, down from 7,718 in September. '),
                            em('Fall Guys'),
                            text(' drew 4,276, its fewest in any full month we have tracked.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'Among Us', '7,574', '12,548'],
                                ['2', 'Lethal Company', '4,809', '7,831'],
                                ['3', 'MECCHA CHAMELEON', '4,382', '5,656'],
                                ['4', 'Fall Guys', '4,276', '9,594'],
                                ['5', 'Super Smash Bros. Ultimate', '3,624', '12,237'],
                                ['6', 'Gartic Phone', '2,588', '4,506'],
                                ['7', 'Sonic Racing: CrossWorlds', '2,224', '5,732'],
                                ['8', 'Jackbox Party Packs', '1,977', '3,974'],
                                ['9', 'PlateUp!', '1,960', '3,493'],
                                ['10', 'Mario Kart World', '1,868', '4,860'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' streamers came back most often for another month, with each seen live an average of 13.4 times. That dipped from 14.5 in September as hundreds of German newcomers arrived at once. '),
                        ]),
                        p([
                            em('Wortgewandt'),
                            text(', a German word game, has gained streamers every month since April, from 21 to 109, even though three channels made up about half of its October streams.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'nanos world', '13.4', '8,347', '623'],
                                ['2', 'Musical Vibes', '13.0', '649', '50'],
                                ['3', 'Words On Stream', '12.5', '6,585', '527'],
                                ['4', 'Ragnarok Origin: Classic', '10.9', '523', '48'],
                                ['5', 'Gartic on Stream', '9.5', '464', '49'],
                                [
                                    '6',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '7.9',
                                    '262',
                                    '33',
                                ],
                                ['7', 'Trombone Champ', '7.1', '207', '29'],
                                ['8', 'Mafia Online', '7.1', '157', '22'],
                                ['9', 'Placid Plastic Duck Simulator', '6.9', '1,554', '226'],
                                ['10', 'Wortgewandt', '6.9', '747', '109'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Nintendo Switch Sports Resort'),
                            text(' launched on October 22 and reached 1,941 streams from 1,056 streamers in 10 days. Launch day was its busiest, with 403 streams averaging 133 viewers. Its audience was spread across many channels, but the biggest launch-night stream was by the Brazilian streamer '),
                            link('https://twitch.tv/alanzoka', 'alanzoka'),
                            text('. Interest cooled after the first few days, from about 48 streams live at a time to about 20 by the end of October.'),
                        ]),
                        p([
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(', a popular English-language variety streamer, played three of the month’s releases: '),
                            em('Nintendo Switch Sports Resort'),
                            text(', '),
                            em('Chained Beasts'),
                            text(', and '),
                            em('Punch Upon a Time'),
                            text('. Northernlion’s streams drew 27% of the viewership for '),
                            em('Chained Beasts'),
                            text(' and 44% of the viewership for '),
                            em('Punch Upon a Time'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    'Nintendo Switch Sports Resort',
                                    'Oct 22',
                                    '1,941',
                                    '1,056',
                                    '63',
                                ],
                                ['Chained Beasts', 'Oct 15', '1,338', '869', '31'],
                                ['Mimic Busters', 'Oct 15', '197', '180', '37'],
                                ['Punch Upon a Time', 'Oct 26', '119', '79', '208'],
                                ['Catch Meow if You Can', 'Oct 29', '69', '54', '8'],
                                ['Outcast Tales', 'Oct 26', '34', '16', '10'],
                                ['Hello Kitty: Party Land', 'Oct 29', '31', '29', '31'],
                                ['Bumper Bout', 'Oct 29', '20', '5', '3'],
                                ['Dub Party', 'Oct 2', '11', '11', '9'],
                                ['Duel: The Game Show', 'Oct 27', '7', '7', '19'],
                                ['Dub Night Live', 'Oct 13', '5', '3', '3'],
                                ['PlantUp', 'Oct 8', '4', '4', '10'],
                                ['Phobos', 'Oct 14', '3', '3', '2'],
                                ['Knuckle Creek', 'Oct 29', '2', '2', '10'],
                                ['Fatal Train', 'Oct 15', '1', '1', '7'],
                                [
                                    'The Cat in the Hat: Rainy Day Mayhem',
                                    'Oct 30',
                                    '1',
                                    '1',
                                    '1',
                                ],
                                ['The Jackbox Party Pack 12', 'Oct 15', '1', '1', '1'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('Our tracker did not record Twitch activity for 30 other party games released in October. They are listed below by release date.'),
                        list({
                            items: [
                                'Beta Bar (Oct 1)',
                                'No Witnesses (Oct 1)',
                                'The Voice Party (Oct 4)',
                                'Veillombre (Oct 5)',
                                "King's Cup (Oct 6)",
                                'Ship Shape Slam!!! (Oct 8)',
                                'American Cooking Simulator: Multiplayer (Oct 12)',
                                'Don Pepe & His Balloons (Oct 15)',
                                'Silly Souls (Oct 15)',
                                'Take My Bomb (Oct 16)',
                                "Who's Cheating? (Oct 16)",
                                'Letrazo (Oct 17)',
                                'Sloptoberfest (Oct 20)',
                                'CrazyFood (Oct 21)',
                                'Sawad (Oct 22)',
                                'Holy Shroom! (Oct 23)',
                                'Screen Cross (Oct 23)',
                                'ByeByeObake! (Oct 26)',
                                'Goddle (Oct 27)',
                                'Outvote (Oct 27)',
                                'Tokyo Wanted (Oct 27)',
                                'Go Dub Yourself! (Oct 29)',
                                'Smithonion Natural History Museum (Oct 29)',
                                'The Dungeon Club (Oct 29)',
                                'White Box Quiz (Oct 29)',
                                'Call Me, Liar (Oct 30)',
                                'Fart Together (Oct 30)',
                                'Shared RC (Oct 30)',
                                'The Trivia Show (Oct 30)',
                                'WAZATO: Sabotage or Mistake? (Oct 30)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The most active channels were live nearly every time we checked in October. Several of them are rerun channels. '),
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' replays old '),
                            link('https://twitch.tv/mrllamasc', 'MrLlamaSC'),
                            text(' '),
                            em('Diablo II: Resurrected'),
                            text(' streams, '),
                            link('https://twitch.tv/telinha', 'Telinha'),
                            text(' marks its '),
                            em('Fall Guys'),
                            text(' streams as reruns, and '),
                            link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                            text(' shows '),
                            em('Super Smash Bros. Ultimate'),
                            text(' tournament matches around the clock. '),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/toxeeec', 'toxeeec')],
                                    '211',
                                    '101',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '211',
                                    '87',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '211',
                                    '11',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '210',
                                    '138',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '210',
                                    '16',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '208',
                                    '22',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '207',
                                    '40',
                                    '1',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/turtlesandgaming', 'TurtlesAndGaming'),
                                    ],
                                    '142',
                                    '26',
                                    '2',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/yoonessa', 'YooNessa')],
                                    '136',
                                    '15',
                                    '6',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/cheese', 'cheese')],
                                    '108',
                                    '103',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 observations',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/auronplay', 'auronplay'),
                            text(' and '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' drew the biggest audiences, both in '),
                            em('Among Us'),
                            text('. The Spanish streamer auronplay’s “legendary mode” stream on October 4 had 222,206 viewers, 5% of all viewership we recorded in October. The German streamer eliasn97 opened the month with an eight-hour '),
                            em('Among Us'),
                            text(' event, then played with a regular group on most Thursdays. Together the two drew 59% of the game’s viewership. Without them, '),
                            em('Among Us'),
                            text(' averaged about 27 viewers rather than 65.'),
                        ]),
                        p([
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' drew big audiences across five different games.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Observations',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/auronplay', 'auronplay')],
                                    '142,006',
                                    '2',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '39,553',
                                    '5',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/northernlion', 'Northernlion')],
                                    '8,787',
                                    '9',
                                    '5',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/sonic', 'Sonic')],
                                    '8,096',
                                    '3',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/agustin51', 'agustin51')],
                                    '8,035',
                                    '3',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                    '7,207',
                                    '4',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/valkyrae', 'Valkyrae')],
                                    '6,948',
                                    '5',
                                    '4',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/xrohat', 'xRohat')],
                                    '5,896',
                                    '4',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/cptwillis', 'CptWillis')],
                                    '5,526',
                                    '2',
                                    '2',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa')],
                                    '5,277',
                                    '2',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(', a 24/7 channel where computer players face off in '),
                            em('Mario Party'),
                            text(' and other retro party games, played the widest range of games again: 19, up from 16 in September. '),
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(', which runs videos from the German group PietSmiet around the clock, was close behind with 15 games and averaged 216 viewers.'),
                        ]),
                        p([
                            text('The biggest audience on this list belonged to '),
                            link('https://twitch.tv/hypnoshark', 'hypnoshark'),
                            text(', who speedruns party games: 463 average viewers across seven games, the channel’s best month in our data. Its '),
                            em('Crash Bash'),
                            text(' runs, part of a speedrun subathon in early October, drew a quarter of that game’s viewership and helped the game into the Party Score top 15 for the first time.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Observations',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '19',
                                    '66',
                                    '21',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '15',
                                    '21',
                                    '216',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/retrorecap', 'RETRORECAP')],
                                    '9',
                                    '24',
                                    '19',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/noopssr', 'NoopsSR')],
                                    '9',
                                    '19',
                                    '41',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/userfriendsly', 'UserFriendsly')],
                                    '7',
                                    '22',
                                    '25',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/kusobaki', 'Kusobaki')],
                                    '7',
                                    '18',
                                    '10',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/terste', 'Terste')],
                                    '7',
                                    '16',
                                    '145',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/mario_tv247', 'MARIO_TV247')],
                                    '7',
                                    '16',
                                    '10',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/hypnoshark', 'hypnoshark')],
                                    '7',
                                    '15',
                                    '463',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/zaiyana', 'Zaiyana')],
                                    '7',
                                    '13',
                                    '316',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('English made up 57% of streams. German streams rose from 10,214 to 13,324. The increase came from '),
                            em('nanos world'),
                            text(', which made up '),
                            link('/party-central?visualization=bar&x=game&grain=&from=2026-10-01T00%3A00%3A00.000Z&to=2026-11-01T00%3A00%3A00.000Z&language=de', '30% of German streams'),
                            text(' in October. German streams of other games fell 7% from September.'),
                        ]),
                        p([
                            text('Spanish streams averaged 32 viewers, up from 13 in September, almost entirely because of '),
                            link('https://twitch.tv/auronplay', 'auronplay'),
                            text('’s two '),
                            em('Among Us'),
                            text(' streams. '),
                            link('https://twitch.tv/batora324', 'バトラ'),
                            text(' drew half of Japanese viewership. Italian and Polish averages fell back to 47 and 19 after one-time visits by large channels in September.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '99,087', '38,484', '17'],
                                ['2', 'Spanish', '15,630', '7,552', '32'],
                                ['3', 'German', '13,324', '4,630', '65'],
                                ['4', 'French', '12,335', '4,292', '48'],
                                ['5', 'Japanese', '9,660', '3,723', '57'],
                                ['6', 'Russian', '8,127', '3,864', '18'],
                                ['7', 'Portuguese', '5,471', '2,489', '22'],
                                ['8', 'Chinese', '2,728', '1,218', '38'],
                                ['9', 'Italian', '1,309', '680', '47'],
                                ['10', 'Polish', '923', '484', '19'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__english__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Super Smash Bros. Ultimate', '8,272', '2,390'],
                                        ['2', 'Words On Stream', '5,940', '454'],
                                        ['3', 'Lethal Company', '5,743', '3,477'],
                                        ['4', 'Among Us', '5,500', '3,254'],
                                        ['5', 'Fall Guys', '5,385', '2,417'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Stinker Golf', '1,062', '13'],
                                        ['2', 'NintendoLand', '429', '6'],
                                        ['3', 'WarioWare: Touched!', '258', '5'],
                                        ['4', 'Family Feud', '257', '51'],
                                        ['5', 'Punch Upon a Time', '208', '119'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/toxeeec', 'toxeeec')],
                                            '211',
                                            '101',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '211',
                                            '87',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '211',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/mrpeanut1028', 'MrPeanut1028'),
                                            ],
                                            '211',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '210',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '8,787',
                                            '9',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sonic', 'Sonic')],
                                            '8,096',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/valkyrae', 'Valkyrae')],
                                            '6,948',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/cptwillis', 'CptWillis')],
                                            '5,526',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/realzbluewater', 'RealzBlueWater'),
                                            ],
                                            '4,340',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__spanish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '3,083', '1,889'],
                                        ['2', 'Super Smash Bros. Ultimate', '1,798', '586'],
                                        ['3', 'Fall Guys', '1,223', '669'],
                                        ['4', 'Mario Kart World', '802', '295'],
                                        ['5', 'Gartic Phone', '770', '477'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Wii Sports', '436', '15'],
                                        ['2', 'Super Mario Strikers', '271', '19'],
                                        ['3', 'Just Dance 2026 Edition', '135', '39'],
                                        ['4', 'Bottle Cracks', '118', '5'],
                                        ['5', 'Among Us', '112', '3,083'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/jaackieboyyy', 'jaackieboyyy'),
                                            ],
                                            '39',
                                            '10',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vizard39', 'vizard39')],
                                            '32',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '30',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/unvitox', 'unVitox')],
                                            '26',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/tonimontanaatv', 'Tonimontanaatv'),
                                            ],
                                            '24',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/auronplay', 'auronplay')],
                                            '142,006',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/agustin51', 'agustin51')],
                                            '8,035',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/djmariio', 'DjMaRiiO')],
                                            '5,032',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '3,428',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,393',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__german__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '3,964', '309'],
                                        ['2', 'Among Us', '1,344', '753'],
                                        ['3', 'MECCHA CHAMELEON', '852', '573'],
                                        ['4', 'Wortgewandt', '747', '109'],
                                        ['5', 'Fall Guys', '702', '316'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'PICO PARK 2', '482', '13'],
                                        ['2', 'Among Us', '242', '1,344'],
                                        ['3', 'nanos world', '93', '3,964'],
                                        ['4', 'Just Dance 2026 Edition', '76', '19'],
                                        ['5', 'Dale & Dawson Stationery Supplies', '63', '10'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/yoonessa', 'YooNessa')],
                                            '136',
                                            '15',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/das__mufflon', 'Das__Mufflon'),
                                            ],
                                            '95',
                                            '44',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '66',
                                            '21',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/itsnoryxx', 'ItsNoryxx')],
                                            '63',
                                            '114',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/dehackxas_ttv', 'DeHackxas_TTV'),
                                            ],
                                            '62',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '39,553',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '7,207',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xrohat', 'xRohat')],
                                            '5,896',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/exsl95', 'Exsl95')],
                                            '4,254',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/coachlim', 'coachlim')],
                                            '3,786',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__french__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '4,194', '298'],
                                        ['2', 'Among Us', '939', '589'],
                                        ['3', 'Fall Guys', '737', '328'],
                                        ['4', 'Super Smash Bros. Ultimate', '695', '220'],
                                        ['5', 'Mario Kart World', '598', '238'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Maitre des Fleurs', '1,346', '13'],
                                        ['2', 'Night Club Simulator: Party King', '475', '6'],
                                        ['3', 'First Class Trouble', '451', '46'],
                                        ['4', 'An Eggstremely Hard Game', '338', '16'],
                                        ['5', 'KLETKA', '270', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/anas6424v1', 'anas6424v1')],
                                            '206',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '84',
                                            '155',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kyle_offf', 'kyle_offf')],
                                            '70',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/abaraii', 'Abaraii')],
                                            '64',
                                            '48',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kankyotv', 'kankyotv')],
                                            '64',
                                            '23',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,402',
                                            '12',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '3,164',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/gom4rt', 'Gom4rt')],
                                            '2,832',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '2,755',
                                            '37',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/hctuan', 'Hctuan')],
                                            '1,816',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__japanese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '1,951', '648'],
                                        ['2', 'Super Smash Bros. Ultimate', '841', '214'],
                                        ['3', 'Beat Saber', '589', '135'],
                                        ['4', 'MECCHA CHAMELEON', '585', '466'],
                                        ['5', 'Mario Kart World', '495', '135'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '2,354', '41'],
                                        ['2', 'Heave Ho 2', '893', '6'],
                                        ['3', 'Mario Kart World', '617', '495'],
                                        ['4', 'diffiCULT', '527', '8'],
                                        ['5', 'Deduce Together', '404', '36'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/bikzooo', 'びくぞー')],
                                            '54',
                                            '16',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/batora324', 'バトラ')],
                                            '53',
                                            '5,174',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/makosu', 'makosu')],
                                            '48',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/rye_nekomiya', '猫宮りぃ')],
                                            '46',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                            '42',
                                            '153',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/batora324', 'バトラ')],
                                            '5,174',
                                            '53',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/hanjoudesu', 'はんじょう')],
                                            '2,088',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/assarisyoko1', 'あっさりしょこー'),
                                            ],
                                            '1,853',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/bakumatsu_shishi', '幕末志士'),
                                            ],
                                            '1,423',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kokujintv', 'kokujintv')],
                                            '1,138',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__russian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Gartic Phone', '1,341', '682'],
                                        ['2', 'Jackbox Party Packs', '712', '248'],
                                        ['3', 'Diablo II: Resurrected', '692', '115'],
                                        ['4', 'Lethal Company', '588', '255'],
                                        ['5', 'SIGame', '522', '345'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Casino', '398', '33'],
                                        ['2', 'The House Always Wins', '224', '6'],
                                        ['3', 'Barnyard', '112', '6'],
                                        ['4', 'Chained Beasts', '95', '92'],
                                        ['5', 'Goose Goose Duck', '79', '345'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/meowkitya', 'meowkitya')],
                                            '39',
                                            '12',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mr_babijohn', 'Mr_BabiJohn')],
                                            '32',
                                            '19',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/hc_fm', 'hc_fm')],
                                            '30',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/molya_requiem', 'molya_requiem'),
                                            ],
                                            '30',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vangavgav', 'vangavgav')],
                                            '26',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/orkpod', 'orkpod')],
                                            '4,439',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/c_a_k_e', 'C_a_k_e')],
                                            '2,750',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/karmikkoala', 'KarmikKoala')],
                                            '1,544',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vika_karter', 'vika_karter')],
                                            '1,181',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/f1ashko', 'F1ashko')],
                                            '780',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__portuguese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '534', '154'],
                                        ['2', 'Ragnarok Origin: Classic', '470', '33'],
                                        ['3', 'Among Us', '347', '234'],
                                        ['4', 'Words On Stream', '345', '44'],
                                        ['5', 'Stumble Guys', '279', '74'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Nintendo Switch Sports Resort', '569', '28'],
                                        ['2', 'Slots Royale: 777 Casino Games', '308', '6'],
                                        ['3', 'Whatsamusic', '213', '8'],
                                        ['4', 'Crash Bash', '142', '9'],
                                        ['5', 'Fall Guys', '60', '534'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '210',
                                            '138',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/maepistolaa', 'MaePistolaa')],
                                            '87',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/blueyroo', 'blueyroo')],
                                            '85',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/scdzeus', 'scdzeus')],
                                            '65',
                                            '28',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/africaalbion', 'AfricaAlbion'),
                                            ],
                                            '61',
                                            '57',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/smzinho', 'Smzinho')],
                                            '952',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/zanfas_cenegal', 'Zanfas_Cenegal'),
                                            ],
                                            '806',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/merlinov7', 'merlinov7')],
                                            '317',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/stereoraze', 'stereoraze')],
                                            '290',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/fabiocog', 'FabioCOG')],
                                            '214',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__chinese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '632', '247'],
                                        ['2', 'Mabinogi', '198', '37'],
                                        ['3', 'MECCHA CHAMELEON', '192', '139'],
                                        ['4', 'Party Animals', '177', '88'],
                                        ['5', 'Goose Goose Duck', '150', '108'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Galumb', '1,455', '6'],
                                        ['2', "Liar's Bar", '229', '16'],
                                        ['3', 'Chained Beasts', '193', '47'],
                                        ['4', 'Overcooked! 2', '179', '58'],
                                        ['5', 'Super Battle Golf', '169', '42'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/aldia1204', '艾爾狄亞')],
                                            '35',
                                            '10',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/daaafuuu', '草莓紅豆大福')],
                                            '30',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/maxdeng', '新八姬')],
                                            '23',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/raven_ouo', '渡夜爵')],
                                            '22',
                                            '10',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/wisdom637', '心靈怪')],
                                            '20',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/yuzumi_neon', '橙ゆずみ_霓')],
                                            '3,102',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '2,858',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/p1lepe1el1', '珮蕾')],
                                            '2,236',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mayj0501', '梅婕')],
                                            '1,534',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/wannasinging', '老王')],
                                            '1,390',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__italian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '200', '92'],
                                        ['2', 'Among Us', '154', '92'],
                                        ['3', 'Neighbors: Suburban Warfare', '98', '66'],
                                        ['4', 'Super Smash Bros. Ultimate', '62', '29'],
                                        ['5', 'Gartic Phone', '60', '46'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Golf With Your Friends', '205', '34'],
                                        ['2', 'Mario Kart 8 Deluxe', '108', '31'],
                                        ['3', 'Nintendo Switch Sports Resort', '99', '13'],
                                        ['4', 'Dale & Dawson Stationery Supplies', '91', '6'],
                                        ['5', 'Live Bingo', '78', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '25',
                                            '188',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/marcomonkey93', 'MarcoMonkey93'),
                                            ],
                                            '22',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '20',
                                            '32',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/lexandzr', 'lexandzr')],
                                            '19',
                                            '11',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/bysamizz', 'bysamizz')],
                                            '14',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '5,277',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/itermosifoni', 'iTermosifoni'),
                                            ],
                                            '237',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/marcuskron', 'MarcusKron')],
                                            '202',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '188',
                                            '25',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ennesimo_', 'Ennesimo_')],
                                            '138',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__polish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '160', '38'],
                                        ['2', 'Among Us', '139', '83'],
                                        ['3', 'Fall Guys', '85', '21'],
                                        ['4', 'Lethal Company', '78', '46'],
                                        ['5', 'SOS OPS!', '44', '41'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Pummel Party', '87', '11'],
                                        ['2', 'Among Us', '49', '139'],
                                        ['3', 'UNO', '45', '8'],
                                        ['4', 'Golf With Your Friends', '25', '23'],
                                        ['5', 'Whatsamusic', '24', '15'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '15',
                                            '24',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/karciarz_a8', 'Karciarz_A8')],
                                            '10',
                                            '23',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/eybiak', 'Eybiak')],
                                            '9',
                                            '56',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/badgeonly', 'BadgeOnly')],
                                            '9',
                                            '22',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/luidzik', 'Luidzik')],
                                            '9',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nexe_', 'Nexe_')],
                                            '2,654',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/michu_hm', 'Michu_HM')],
                                            '201',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/axia', 'Axia')],
                                            '190',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vdr1984', 'Vdr1984')],
                                            '172',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lemonalivee', 'LemonaLivee')],
                                            '147',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            sections: [
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('8,347 streams | 623 streamers | 13.4 stickiness | 87 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '84',
                                            '155',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kyle_offf', 'kyle_offf')],
                                            '70',
                                            '14',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/abaraii', 'Abaraii')],
                                            '64',
                                            '48',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kankyotv', 'kankyotv')],
                                            '64',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/itsnoryxx', 'ItsNoryxx')],
                                            '63',
                                            '114',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '2,755',
                                            '37',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/onlyjson', 'onlyjson')],
                                            '2,038',
                                            '40',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/edizderbreite', 'edizderbreite'),
                                            ],
                                            '1,357',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '1,202',
                                            '43',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xrick75', 'xrick75')],
                                            '811',
                                            '48',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-world',
                    title: 'Mario Kart World',
                    collapsible: true,
                    blocks: [
                        p('4,860 streams | 1,868 streamers | 2.6 stickiness | 83 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/l1ammk', 'l1ammk')],
                                            '80',
                                            '81',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vantox_', 'Vantox_')],
                                            '60',
                                            '46',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/bikzooo', 'びくぞー')],
                                            '54',
                                            '16',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/batora324', 'バトラ')],
                                            '53',
                                            '5,174',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/toutgamerkonekte1', 'toutgamerkonekte1'),
                                            ],
                                            '42',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/batora324', 'バトラ')],
                                            '5,174',
                                            '53',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '3,543',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '2,206',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hanjoudesu', 'はんじょう')],
                                            '2,088',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/assarisyoko1', 'あっさりしょこー'),
                                            ],
                                            '1,853',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__sonic-racing-crossworlds',
                    title: 'Sonic Racing: CrossWorlds',
                    collapsible: true,
                    blocks: [
                        p('5,732 streams | 2,224 streamers | 2.6 stickiness | 22 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__sonic-racing-crossworlds__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/wowitzmaazi', 'WowItzMaazi')],
                                            '46',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/yooopie92', 'Yooopie92')],
                                            '43',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/garomask_', 'garomask_')],
                                            '36',
                                            '23',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/sxntbsuper', 'sxntbsuper')],
                                            '35',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ukastra', 'UKAstra')],
                                            '33',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__sonic-racing-crossworlds__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sonic', 'Sonic')],
                                            '8,096',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/arielle', 'Arielle')],
                                            '2,583',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/axialmatt', 'AxialMatt')],
                                            '763',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vulpeshd', 'VulpesHD')],
                                            '644',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/gamescage', 'GamesCage')],
                                            '508',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__jackbox-party-packs',
                    title: 'Jackbox Party Packs',
                    collapsible: true,
                    blocks: [
                        p('3,974 streams | 1,977 streamers | 2 stickiness | 34 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__jackbox-party-packs__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/youplaylive', 'youplaylive')],
                                            '186',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/chaotik_lord', 'chaotik_lord'),
                                            ],
                                            '81',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/djmysterium', 'DjMysterium')],
                                            '33',
                                            '13',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/andojeebo', 'AndoJeebo')],
                                            '30',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/mattitude1233', 'Mattitude1233'),
                                            ],
                                            '29',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__jackbox-party-packs__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '8,559',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/dooleynoted', 'DooleyNoted')],
                                            '1,175',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/aplatypuss', 'APlatypuss')],
                                            '1,002',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/murdercrumpet', 'MurderCrumpet'),
                                            ],
                                            '945',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/skaifox', 'Skaifox')],
                                            '691',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('6,627 streams | 1,181 streamers | 5.6 stickiness | 15 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '211',
                                            '87',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/devildimlight_ttv', 'DevilDimLight_TTV'),
                                            ],
                                            '90',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kahplunk', 'Kahplunk')],
                                            '75',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/technodudu', 'TechnoDudu')],
                                            '54',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/d2kariya', 'd2kariya')],
                                            '50',
                                            '25',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '606',
                                            '23',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/lordpappnase', 'LordPappnase'),
                                            ],
                                            '482',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/steelmage', 'Steelmage')],
                                            '390',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kano', 'Kano')],
                                            '296',
                                            '17',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/mistersleepycat', 'MisterSleepyCat'),
                                            ],
                                            '274',
                                            '11',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('6,585 streams | 527 streamers | 12.5 stickiness | 14 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '211',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '208',
                                            '22',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '207',
                                            '40',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '173',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/turtlesandgaming', 'TurtlesAndGaming'),
                                            ],
                                            '116',
                                            '26',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/linkzzey', 'Linkzzey')],
                                            '572',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/viperous', 'Viperous')],
                                            '266',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/graecie', 'Graecie')],
                                            '252',
                                            '30',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/jotta', 'jotta')],
                                            '163',
                                            '46',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/buni', 'Buni')],
                                            '156',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('602 streams | 149 streamers | 4 stickiness | 20 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '17',
                                            '46',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/t4ki1980', 'T4ki1980')],
                                            '16',
                                            '22',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/steelvalleypinball', 'SteelValleyPinball'),
                                            ],
                                            '15',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/enteryourinitials', 'EnterYourInitials'),
                                            ],
                                            '14',
                                            '39',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/whatsbrewing', 'whatsbrewing'),
                                            ],
                                            '14',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '317',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/electricbatarcade', 'ElectricBatArcade'),
                                            ],
                                            '53',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/inkandmos', 'INKandMOS')],
                                            '47',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '46',
                                            '17',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sdtmpinball', 'SDTMpinball')],
                                            '45',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__musical-vibes',
                    title: 'Musical Vibes',
                    collapsible: true,
                    blocks: [
                        p('649 streams | 50 streamers | 13 stickiness | 34 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__musical-vibes__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/toxeeec', 'toxeeec')],
                                            '211',
                                            '101',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/marianica78', 'MARIANICA78')],
                                            '8',
                                            '18',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/chico_da_mann_', 'Chico_da_Mann_'),
                                            ],
                                            '6',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/kingharville007', 'kingharville007'),
                                            ],
                                            '3',
                                            '15',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/chaos_cakes', 'Chaos_Cakes')],
                                            '1',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__musical-vibes__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/toxeeec', 'toxeeec')],
                                            '101',
                                            '211',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/marianica78', 'MARIANICA78')],
                                            '18',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/kingharville007', 'kingharville007'),
                                            ],
                                            '15',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/chico_da_mann_', 'Chico_da_Mann_'),
                                            ],
                                            '6',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/djd_ave', 'djd_ave')],
                                            '4',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__among-us',
                    title: 'Among Us',
                    collapsible: true,
                    blocks: [
                        p('12,548 streams | 7,574 streamers | 1.7 stickiness | 65 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__among-us__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/qawiy1999', 'qawiy1999')],
                                            '24',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thepango0', 'ThePango0')],
                                            '21',
                                            '16',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/mike_f4bi4n2000', 'mike_f4bi4n2000'),
                                            ],
                                            '21',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bellabiever', 'bellabiever')],
                                            '20',
                                            '35',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '20',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__among-us__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/auronplay', 'auronplay')],
                                            '142,006',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '39,553',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/agustin51', 'agustin51')],
                                            '8,035',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '7,207',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xrohat', 'xRohat')],
                                            '5,896',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__fall-guys',
                    title: 'Fall Guys',
                    collapsible: true,
                    blocks: [
                        p('9,594 streams | 4,276 streamers | 2.2 stickiness | 20 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '210',
                                            '138',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/melonbearttv', 'melonbearttv'),
                                            ],
                                            '56',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/itstime4war_', 'iTsTiMe4WaR_'),
                                            ],
                                            '43',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/s_sebinho89', 's_sebinho89')],
                                            '42',
                                            '12',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/basti2003ttv', 'basti2003ttv'),
                                            ],
                                            '42',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/realzbluewater', 'RealzBlueWater'),
                                            ],
                                            '4,340',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/chrismelberger', 'chrismelberger'),
                                            ],
                                            '966',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/julien', 'julien')],
                                            '852',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/whatifjulia', 'WhatifJulia')],
                                            '592',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/callumfromthecorner', 'CallumFromTheCorner'),
                                            ],
                                            '560',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Saturday and Sunday were the busiest streaming days. Thursday and Sunday had the highest average viewership, thanks to two streamers: '),
                            link('https://twitch.tv/auronplay', 'auronplay'),
                            text('’s biggest '),
                            em('Among Us'),
                            text(' stream fell on Sunday, October 4, and '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' played '),
                            em('Among Us'),
                            text(' on most Thursdays.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '975', '34'],
                                ['Monday', '705', '24'],
                                ['Tuesday', '700', '32'],
                                ['Wednesday', '701', '24'],
                                ['Thursday', '713', '36'],
                                ['Friday', '848', '25'],
                                ['Saturday', '1,048', '19'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Streaming peaked during October at 20:00 UTC, with 1,291 streams live at a time, and was quietest from 08:00 to 10:00 UTC, with about 375. Average viewership spiked to 50 at both the 17:00 and 20:00 timeslots. '),
                            link('https://twitch.tv/auronplay', 'auronplay'),
                            text('’s October 4 stream fell in the 20:00 hour. At 17:00, the big audiences came from another auronplay stream and from three Japanese streamers, '),
                            link('https://twitch.tv/kato_junichi0817', '加藤純一うん〇ちゃん'),
                            text(', '),
                            link('https://twitch.tv/stylishnoob4', 'stylishnoob4'),
                            text(', and '),
                            link('https://twitch.tv/fps_shaka', 'fps_shaka'),
                            text(', who played '),
                            em('Super Battle Golf'),
                            text(' together in a late-night after-party session.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,159', '24'],
                                ['01:00', '1,153', '16'],
                                ['02:00', '1,204', '15'],
                                ['03:00', '1,099', '17'],
                                ['04:00', '780', '24'],
                                ['05:00', '718', '15'],
                                ['06:00', '576', '19'],
                                ['07:00', '429', '14'],
                                ['08:00', '380', '18'],
                                ['09:00', '375', '19'],
                                ['10:00', '376', '18'],
                                ['11:00', '387', '24'],
                                ['12:00', '502', '25'],
                                ['13:00', '649', '21'],
                                ['14:00', '661', '21'],
                                ['15:00', '675', '28'],
                                ['16:00', '810', '32'],
                                ['17:00', '942', '50'],
                                ['18:00', '990', '43'],
                                ['19:00', '1,219', '27'],
                                ['20:00', '1,291', '50'],
                                ['21:00', '1,128', '26'],
                                ['22:00', '1,013', '37'],
                                ['23:00', '1,143', '29'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-10-top-party-games-and-streamers.js.map